<template>
  <div class="mt-4">
    <div v-if="loading" class="d-flex justify-content-center py-4">
      <loading-spinner />
    </div>

    <template v-else-if="stats">
      <!-- Row 1: stat cards -->
      <div class="row g-3 mb-3">
        <div class="col-md-6 col-12">
          <div class="stat-card">
            <div class="stat-label">Avg Days to Sell</div>
            <div class="stat-value">{{ stats.avg_days_to_sell != null ? Math.round(stats.avg_days_to_sell) : '—' }}</div>
            <em class="small text-muted">{{ stats.matched_count }} items
              <span v-if="parseInt(stats.unmatched_count) > 0"> ({{ stats.unmatched_count }} unmatched)</span>
            </em>
            <range-comparison v-if="comparisonStats?.avg_days_to_sell != null" green-direction="down" :curr-val="parseFloat(stats.avg_days_to_sell)" :prev-val="parseFloat(comparisonStats.avg_days_to_sell)" format="float" :show-comparison-label="true" />
            <div class="stat-icon text-secondary"><i class="bi bi-graph-up-arrow"></i></div>
          </div>
        </div>
        <div class="col-md-6 col-12">
          <div class="stat-card">
            <div class="stat-label">Median Days to Sell</div>
            <div class="stat-value">{{ stats.median_days_to_sell != null ? Math.round(stats.median_days_to_sell) : '—' }}</div>
            <em class="small text-muted">{{ stats.matched_count }} items
              <span v-if="parseInt(stats.unmatched_count) > 0"> ({{ stats.unmatched_count }} unmatched)</span>
            </em>
            <range-comparison v-if="comparisonStats?.median_days_to_sell != null" green-direction="down" :curr-val="parseFloat(stats.median_days_to_sell)" :prev-val="parseFloat(comparisonStats.median_days_to_sell)" format="float" />
            <div class="stat-icon text-secondary"><i class="bi bi-circle-half"></i></div>
          </div>
        </div>
      </div>

      <!-- Row 2: charts -->
      <div class="row g-3">
        <div class="col-lg-6 col-12">
          <div class="chart-card">
            <h6 class="chart-title">Items Sold by Time Listed</h6>
            <apexchart
              type="bar"
              height="220"
              :options="countChartOptions"
              :series="countSeries"
            />
          </div>
        </div>
        <div class="col-lg-6 col-12">
          <div class="chart-card">
            <h6 class="chart-title">Gross Sales by Time Listed</h6>
            <apexchart
              type="bar"
              height="220"
              :options="revenueChartOptions"
              :series="revenueSeries"
            />
          </div>
        </div>
      </div>
    </template>

    <div v-else class="text-muted small py-3">No sell speed data for this period.</div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineAsyncComponent, defineProps } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from '@/app/components/Utils/LoadingSpinner.vue'
import { DateUtil } from '@/services/dateUtils'
import RangeComparison from '@/app/components/Utils/RangeComparison.vue'

const apexchart = defineAsyncComponent(() =>
  import('vue3-apexcharts').then(m => m.default || m)
)

const props = defineProps({
  range: { type: Object, required: true }
})

const store = useStore()
const loading = ref(false)
const stats = ref(null)
const comparisonStats = ref(null)

const comparisonMode = computed(() => props.range?.comparison || 'prev')

function getComparisonDates() {
  const from = props.range.from
  const to = props.range.to
  if (comparisonMode.value === 'yoy') {
    return {
      from: from?.minus ? from.minus({ years: 1 }) : new Date(new Date(from).setFullYear(new Date(from).getFullYear() - 1)),
      to: to?.minus ? to.minus({ years: 1 }) : new Date(new Date(to).setFullYear(new Date(to).getFullYear() - 1))
    }
  }
  const spanDays = from?.diff
    ? Math.floor(to.startOf('day').diff(from.startOf('day'), 'days').days) + 1
    : Math.floor((new Date(to).setHours(0,0,0,0) - new Date(from).setHours(0,0,0,0)) / 86400000) + 1
  return {
    from: from?.minus ? from.minus({ days: spanDays }) : new Date(new Date(from).getTime() - spanDays * 86400000),
    to: to?.minus ? to.minus({ days: spanDays }) : new Date(new Date(to).getTime() - spanDays * 86400000)
  }
}

async function fetchComparisonData() {
  try {
    const { from, to } = getComparisonDates()
    const response = await store.state.api.get('/inventory/aggregate-sale-velocity', {
      params: { from: dateUtil.value.toISODate(from), to: dateUtil.value.toISODate(to) }
    })
    const rows = response.data?.data
    comparisonStats.value = Array.isArray(rows) && rows.length > 0 ? rows[0] : null
  } catch {
    comparisonStats.value = null
  }
}

const storeConfig = computed(() => store.state.eshop.eshop)
const dateUtil = computed(() => new DateUtil(storeConfig.value?.timezone || store.state.defaultTimezone))

const BUCKETS = [
  { key: 'bucket_0_7',     pct: 'pct_0_7',     rev: 'rev_0_7',     label: '≤ 7 days',    color: '#7dd89a' },
  { key: 'bucket_8_30',    pct: 'pct_8_30',    rev: 'rev_8_30',    label: '8–30 days',   color: '#b0d96e' },
  { key: 'bucket_31_90',   pct: 'pct_31_90',   rev: 'rev_31_90',   label: '31–90 days',  color: '#e8d454' },
  { key: 'bucket_91_180',  pct: 'pct_91_180',  rev: 'rev_91_180',  label: '91–180 days', color: '#fcc07a' },
  { key: 'bucket_181_365', pct: 'pct_181_365', rev: 'rev_181_365', label: '181–365 days',color: '#f09060' },
  { key: 'bucket_365_plus',pct: 'pct_365_plus',rev: 'rev_365_plus',label: '365+ days',   color: '#e88080' },
]

const bucketCategories = BUCKETS.map(b => b.label)
const bucketColors = BUCKETS.map(b => b.color)
const totalRev = computed(() => stats.value ? parseFloat(stats.value.total_revenue): 0)

const countSeries = computed(() => [{
  name: 'Items',
  data: BUCKETS.map(b => stats.value?.[b.key] ?? 0)
}])

const revenueSeries = computed(() => [{
  name: 'Revenue',
  data: BUCKETS.map(b => parseFloat(stats.value?.[b.rev] ?? 0))
}])

const sharedOptions = {
  chart: {
    type: 'bar',
    toolbar: { show: false },
    animations: { enabled: true },
  },
  plotOptions: {
    bar: {
      barHeight: '95%',
      borderRadius: 2,
      horizontal: true,
      distributed: true,
      dataLabels: { position: 'top' },
    }
  },
  colors: bucketColors,
  legend: { show: false },
  xaxis: {
    categories: bucketCategories,
    labels: { formatter: (val) => Math.round(val) }
  },
  yaxis: {
    labels: { style: { fontSize: '12px' } }
  },
  grid: {
    borderColor: '#e0e0e0',
    strokeDashArray: 4,
  },
}

const countChartOptions = computed(() => ({
  ...sharedOptions,
  dataLabels: {
    enabled: true,
    textAnchor: 'start',
    offsetX: 15,
    style: {
      fontSize: '12px',
      colors: ['#000']
    },
    dropShadow: {
      enabled: true,
      top: 0,
      left: 0,
      blur: 2,
      color: '#fff',
      opacity: 1
    },
    formatter: (val, { dataPointIndex }) => {
      const pct = Math.round(stats.value?.[BUCKETS[dataPointIndex].pct])
      return pct != null ? `${val}  (${pct}%)` : `${val}`
    }
  },
  tooltip: {
    y: {
      formatter: (val, { dataPointIndex }) => {
        const pct = stats.value?.[BUCKETS[dataPointIndex].pct]
        return pct != null ? `${val} items (${pct}%)` : `${val} items`
      }
    }
  },
}))

const revenueChartOptions = computed(() => ({
  ...sharedOptions,
  dataLabels: {
    enabled: true,
    textAnchor: 'start',
    offsetX: 6,
    style: {
      fontSize: '12px',
      colors: ['#000']
    },
    dropShadow: {
      enabled: true,
      top: 0,
      left: 0,
      blur: 2,
      color: '#fff',
      opacity: 1
    },
    formatter: (val) => `$${parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} (${Math.round(parseFloat(val)/totalRev.value*100)}%)`,
  },
  xaxis: {
    ...sharedOptions.xaxis,
    labels: { formatter: (val) => `$${Math.round(val)}` }
  },
  tooltip: {
    y: { formatter: (val) => `$${parseFloat(val).toFixed(2)}` }
  },
}))

async function fetchData() {
  loading.value = true
  try {
    const from = dateUtil.value.toISODate(props.range.from)
    const to = dateUtil.value.toISODate(props.range.to)
    const response = await store.state.api.get('/inventory/aggregate-sale-velocity', { params: { from, to } })
    const rows = response.data?.data
    stats.value = Array.isArray(rows) && rows.length > 0 ? rows[0] : null
  } catch (e) {
    console.error('SellSpeedSummary fetch error:', e)
    stats.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => { fetchData(); fetchComparisonData() })
watch(() => props.range, () => { fetchData(); fetchComparisonData() }, { deep: true })
</script>
